import React, { useState } from 'react';
import type { Issue } from '../../types/issue';
import type { EnterpriseAction } from '../../services/actionService';
import type { StandardizationEventRecord } from '../../services/issueMaspService';
import { efficacyAttempts, evaluateIssueClosure } from '../../utils/efficacyCheck';
import { isCauseConfirmed } from '../../utils/problemCause';
import { executionProgress } from '../../utils/countermeasurePlan';
import {
  AUDIT_RESULT_LABEL,
  STANDARDIZATION_KIND_LABEL,
  formatEventDate,
  standardizationReadiness,
  type AuditResult,
  type StandardizationKind,
} from '../../utils/standardization';

interface StandardizationWorkspaceProps {
  issue: Issue;
  actions: EnterpriseAction[];
  events: StandardizationEventRecord[];
  closed: boolean;
  eventsState?: 'error';
  onAddEvent: (input: {
    kind: StandardizationKind;
    title: string;
    body: string;
    audience: string;
    result: string;
    occurredOn: string;
  }) => Promise<void>;
}

const READINESS_LINES: Array<[keyof ReturnType<typeof standardizationReadiness>, string]> = [
  ['standard', 'Padrão atualizado'],
  ['training', 'Pessoas treinadas'],
  ['communication', 'Comunicação realizada'],
  ['audit', 'Acompanhamento definido'],
];

export const StandardizationWorkspace: React.FC<StandardizationWorkspaceProps> = ({
  issue,
  actions,
  events,
  closed,
  eventsState,
  onAddEvent,
}) => {
  const direct = actions.filter((action) => action.problem_id === issue.id);
  const progress = executionProgress(direct);
  const confirmed = isCauseConfirmed(issue);
  const cause = issue.root_cause_analysis?.root_cause_description?.trim()
    || issue.root_cause?.trim()
    || '';
  const attempts = efficacyAttempts(issue.metadata);
  const closure = evaluateIssueClosure({
    actions: direct,
    attempts,
    closureReason: issue.metadata?.closure_reason,
    metadata: issue.metadata,
  });
  const efficacyValid = closure.case === 'B' && closure.canClose;
  const latest = [...attempts].sort((left, right) => new Date(right.at).getTime() - new Date(left.at).getTime())[0];
  const readiness = standardizationReadiness(events);
  const enabled = efficacyValid && !closed;

  return (
    <div className="max-w-[960px] space-y-4">
      <section>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Causa confirmada</p>
        <p className="mt-0.5 text-[14px] font-medium text-slate-950">
          {confirmed && cause ? cause : 'A causa ainda não está consolidada.'}
        </p>
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Contramedidas</p>
        <p className="mt-0.5 text-[13px] text-slate-800">{progress.completed} de {progress.active} concluídas</p>
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Eficácia</p>
        <p className="mt-0.5 text-[13px] text-slate-800">
          {efficacyValid ? 'Eficaz para as contramedidas concluídas.' : 'Ainda sem eficácia que cubra as contramedidas.'}
        </p>
        {latest ? (
          <p className="text-[12px] text-slate-500">
            Última verificação em {new Date(latest.at).toLocaleDateString('pt-BR')}
          </p>
        ) : null}
      </section>

      <section>
        <h3 className="text-[13px] font-semibold text-slate-900">Prontidão</h3>
        <p className="mt-0.5 text-[13px] text-slate-800">
          {eventsState === 'error' ? 'Não foi possível carregar a padronização.' : `${readiness.recorded} de ${readiness.total} itens registrados`}
        </p>
        <ul className="mt-1">
          {READINESS_LINES.map(([key, label]) => (
            <li key={key} className="text-[12px] text-slate-600">
              {label} · {readiness[key] ? 'registrado' : 'pendente'}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h3 className="text-[13px] font-semibold text-slate-900">Registros</h3>
        {enabled ? (
          <EventForm onAdd={onAddEvent} />
        ) : (
          <p className="mt-1 text-[13px] text-slate-600">A padronização começa depois da eficácia.</p>
        )}
        {events.length > 0 ? (
          <ul className="mt-2 border-t border-slate-200">
            {events.map((event) => (
              <li key={event.id} className="border-b border-slate-100 py-1.5 text-[12px] text-slate-700">
                {eventLine(event)}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
};

function eventLine(event: StandardizationEventRecord): string {
  const date = formatEventDate(event.occurredOn || event.createdAt);
  const detail = event.kind === 'audit'
    ? [event.result ? AUDIT_RESULT_LABEL[event.result] : '', event.title].filter(Boolean).join(' · ')
    : event.kind === 'communication'
      ? (event.audience || event.body || '')
      : (event.title || event.body || '');
  return [STANDARDIZATION_KIND_LABEL[event.kind], date, detail].filter(Boolean).join(' · ');
}

function EventForm({ onAdd }: { onAdd: StandardizationWorkspaceProps['onAddEvent'] }) {
  const [kind, setKind] = useState<StandardizationKind | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('');
  const [result, setResult] = useState<AuditResult | ''>('');
  const [occurredOn, setOccurredOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const open = (next: StandardizationKind) => {
    setKind(next);
    setError('');
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!kind) return;
    setSaving(true);
    setError('');
    try {
      await onAdd({ kind, title, body, audience, result, occurredOn });
      setKind(null);
      setTitle('');
      setBody('');
      setAudience('');
      setResult('');
      setOccurredOn('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível registrar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1">
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px]">
        {(['standard', 'training', 'communication', 'audit'] as const).map((item) => (
          <button key={item} type="button" onClick={() => open(item)} className="font-medium text-slate-700 hover:underline">
            + {STANDARDIZATION_KIND_LABEL[item]}
          </button>
        ))}
      </div>
      {kind ? (
        <form onSubmit={(event) => void submit(event)} className="mt-2 max-w-md space-y-2">
          {kind !== 'communication' ? (
            <label className="block text-[12px] text-slate-600">
              {kind === 'audit' ? 'Título' : STANDARDIZATION_KIND_LABEL[kind]}
              <input
                aria-label={kind === 'audit' ? 'Título do acompanhamento' : STANDARDIZATION_KIND_LABEL[kind]}
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                className="mt-0.5 h-8 w-full rounded border border-slate-300 px-2 text-[13px]"
              />
            </label>
          ) : null}
          {kind === 'communication' ? (
            <label className="block text-[12px] text-slate-600">
              Público
              <input
                aria-label="Público"
                value={audience}
                onChange={(event) => setAudience(event.target.value)}
                className="mt-0.5 h-8 w-full rounded border border-slate-300 px-2 text-[13px]"
              />
            </label>
          ) : null}
          {kind === 'audit' ? (
            <label className="block text-[12px] text-slate-600">
              Resultado
              <select
                aria-label="Resultado do acompanhamento"
                value={result}
                onChange={(event) => setResult(event.target.value as AuditResult | '')}
                className="mt-0.5 h-8 w-full rounded border border-slate-300 px-2 text-[13px]"
              >
                <option value="">Selecione</option>
                <option value="pass">Aprovado</option>
                <option value="fail">Reprovado</option>
                <option value="partial">Parcial</option>
              </select>
            </label>
          ) : null}
          <label className="block text-[12px] text-slate-600">
            {kind === 'communication' ? 'Comunicação' : 'Observação'}
            <textarea
              aria-label={kind === 'communication' ? 'Comunicação' : 'Observação'}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={2}
              className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-[13px]"
            />
          </label>
          <label className="block text-[12px] text-slate-600">
            Data
            <input
              aria-label="Data do registro"
              type="date"
              value={occurredOn}
              onChange={(event) => setOccurredOn(event.target.value)}
              className="mt-0.5 h-8 rounded border border-slate-300 px-2 text-[13px]"
            />
          </label>
          {error ? <p className="text-[12px] text-red-700">{error}</p> : null}
          <div className="flex gap-3 text-[12px]">
            <button type="submit" disabled={saving} className="font-medium text-slate-800 hover:underline disabled:opacity-50">
              {saving ? 'Salvando...' : 'Registrar'}
            </button>
            <button type="button" onClick={() => setKind(null)} className="text-slate-500 hover:underline">Fechar</button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
